/**
 * Club Profile Editor - Club Owner can edit club information
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2, ArrowLeft, Save, MapPin, Phone, Mail, Globe,
  Instagram, Facebook, MessageCircle, Clock, List, Image,
  Plus, X, Loader2, Info
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import api from '../../services/api';
import { toast } from 'sonner';

// Days of week for opening hours
const DAYS = [
  { key: 'mon', label: 'Lunes' },
  { key: 'tue', label: 'Martes' },
  { key: 'wed', label: 'Miércoles' },
  { key: 'thu', label: 'Jueves' },
  { key: 'fri', label: 'Viernes' },
  { key: 'sat', label: 'Sábado' },
  { key: 'sun', label: 'Domingo' },
];

// Common amenities
const COMMON_AMENITIES = [
  'Estacionamiento',
  'Cafetería',
  'Vestidores',
  'Regaderas',
  'Wi-Fi',
  'Tienda deportiva',
  'Alquiler de equipo',
  'Clases/Entrenadores',
  'Área de espera',
  'Televisores',
];

export default function ClubProfileEditor() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [club, setClub] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    instagram: '',
    facebook: '',
    whatsapp: '',
    opening_hours: {},
    amenities: [],
    rules: [],
    images: [],
    logo_url: '',
  });
  
  const [newRule, setNewRule] = useState('');
  const [newAmenity, setNewAmenity] = useState('');

  useEffect(() => {
    fetchClubProfile();
  }, []);

  const fetchClubProfile = async () => {
    try {
      setLoading(true);
      const res = await api.get('/club/profile');
      const clubData = res.data.club;
      setClub(clubData);
      
      // Populate form with existing data
      setFormData({
        name: clubData.name || '',
        description: clubData.description || '',
        address: clubData.address || '',
        phone: clubData.phone || '',
        email: clubData.email || '',
        website: clubData.website || '',
        instagram: clubData.instagram || '',
        facebook: clubData.facebook || '',
        whatsapp: clubData.whatsapp || '',
        opening_hours: clubData.opening_hours || {},
        amenities: clubData.amenities || [],
        rules: clubData.rules || [],
        images: clubData.images || [],
        logo_url: clubData.logo_url || '',
      });
    } catch (err) {
      console.error('Error fetching club:', err);
      toast.error('Error cargando datos del club');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleOpeningHoursChange = (day, value) => {
    setFormData({
      ...formData,
      opening_hours: { ...formData.opening_hours, [day]: value }
    });
  };

  const handleAddAmenity = (amenity) => {
    if (amenity && !formData.amenities.includes(amenity)) {
      setFormData({
        ...formData,
        amenities: [...formData.amenities, amenity]
      });
    }
    setNewAmenity('');
  };

  const handleRemoveAmenity = (index) => {
    setFormData({
      ...formData,
      amenities: formData.amenities.filter((_, i) => i !== index)
    });
  };

  const handleAddRule = () => {
    if (newRule.trim() && !formData.rules.includes(newRule.trim())) {
      setFormData({
        ...formData,
        rules: [...formData.rules, newRule.trim()]
      });
      setNewRule('');
    }
  };

  const handleRemoveRule = (index) => {
    setFormData({
      ...formData,
      rules: formData.rules.filter((_, i) => i !== index)
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Only send non-empty values
      const updateData = {};
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        if (value !== '' && value !== null && value !== undefined) {
          if (Array.isArray(value) && value.length > 0) {
            updateData[key] = value;
          } else if (typeof value === 'object' && Object.keys(value).length > 0) {
            updateData[key] = value;
          } else if (typeof value === 'string' && value.trim()) {
            updateData[key] = value;
          }
        }
      });
      
      await api.patch('/club/profile', updateData);
      toast.success('Perfil del club actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error actualizando perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/club/settings')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg">Perfil del Club</h1>
              <p className="text-xs text-muted-foreground">Edita la información de tu club</p>
            </div>
          </div>
          
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Guardar
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Basic Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-xl border border-border p-6"
        >
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Información Básica
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Nombre del Club *</Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Nombre del club"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Logo URL</Label>
              <Input
                value={formData.logo_url}
                onChange={(e) => handleChange('logo_url', e.target.value)}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Descripción</Label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe tu club, servicios, especialidades..."
                className="w-full mt-1 p-3 rounded-md border border-input bg-background text-sm min-h-[100px]"
              />
            </div>
          </div>
        </motion.div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card rounded-xl border border-border p-6"
        >
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-primary" />
            Contacto
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Dirección</Label>
              <div className="relative mt-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Calle, número, colonia, ciudad"
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <Label>Teléfono</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+52 55 1234 5678"
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="contacto@tuclub.com"
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <Label>WhatsApp</Label>
              <div className="relative mt-1">
                <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={formData.whatsapp}
                  onChange={(e) => handleChange('whatsapp', e.target.value)}
                  placeholder="+52 55 1234 5678"
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Social Media */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card rounded-xl border border-border p-6"
        >
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            Redes Sociales
          </h2>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Sitio Web</Label>
              <div className="relative mt-1">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://tuclub.com"
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <Label>Instagram</Label>
              <div className="relative mt-1">
                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={formData.instagram}
                  onChange={(e) => handleChange('instagram', e.target.value)}
                  placeholder="@tuclub"
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <Label>Facebook</Label>
              <div className="relative mt-1">
                <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={formData.facebook}
                  onChange={(e) => handleChange('facebook', e.target.value)}
                  placeholder="facebook.com/tuclub"
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Opening Hours */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card rounded-xl border border-border p-6"
        >
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Horarios de Operación
          </h2>
          
          <div className="grid md:grid-cols-2 gap-3">
            {DAYS.map(({ key, label }) => (
              <div key={key} className="flex items-center gap-3">
                <span className="w-24 text-sm font-medium">{label}</span>
                <Input
                  value={formData.opening_hours[key] || ''}
                  onChange={(e) => handleOpeningHoursChange(key, e.target.value)}
                  placeholder="07:00 - 22:00 o Cerrado"
                  className="flex-1"
                />
              </div>
            ))}
          </div>
        </motion.div>

        {/* Amenities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-card rounded-xl border border-border p-6"
        >
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <List className="w-5 h-5 text-primary" />
            Amenidades
          </h2>
          
          {/* Quick add common amenities */}
          <div className="flex flex-wrap gap-2 mb-4">
            {COMMON_AMENITIES.filter(a => !formData.amenities.includes(a)).map((amenity) => (
              <button
                key={amenity}
                type="button"
                onClick={() => handleAddAmenity(amenity)}
                className="px-3 py-1 text-xs rounded-full border border-border hover:bg-primary/10 hover:border-primary transition-colors"
              >
                + {amenity}
              </button>
            ))}
          </div>
          
          {/* Custom amenity input */}
          <div className="flex gap-2 mb-4">
            <Input
              value={newAmenity}
              onChange={(e) => setNewAmenity(e.target.value)}
              placeholder="Agregar otra amenidad..."
              onKeyDown={(e) => e.key === 'Enter' && handleAddAmenity(newAmenity)}
            />
            <Button variant="outline" onClick={() => handleAddAmenity(newAmenity)}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Selected amenities */}
          {formData.amenities.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.amenities.map((amenity, idx) => (
                <span
                  key={idx}
                  className="flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                >
                  {amenity}
                  <button onClick={() => handleRemoveAmenity(idx)}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </motion.div>

        {/* Rules */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-card rounded-xl border border-border p-6"
        >
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <Info className="w-5 h-5 text-primary" />
            Reglas del Club
          </h2>
          
          <div className="flex gap-2 mb-4">
            <Input
              value={newRule}
              onChange={(e) => setNewRule(e.target.value)}
              placeholder="Ej: Calzado deportivo obligatorio"
              onKeyDown={(e) => e.key === 'Enter' && handleAddRule()}
            />
            <Button variant="outline" onClick={handleAddRule}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          
          {formData.rules.length > 0 && (
            <ul className="space-y-2">
              {formData.rules.map((rule, idx) => (
                <li key={idx} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                  <span className="text-sm">{rule}</span>
                  <Button variant="ghost" size="icon" onClick={() => handleRemoveRule(idx)}>
                    <X className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </motion.div>
      </div>
    </div>
  );
}
